import React, { useEffect, useState, useContext } from "react";
import { FormContext } from "../Form";
import clsx from "clsx";
import { getTailwindColorClass, Intent } from "../../common/enums";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;

  size?: number;

  variant?: Intent;

  block?: boolean;

  disabled?: boolean;

  type?: "submit" | "reset" | "button";

  validity?: Record<string, boolean>;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      variant,
      block = false,
      disabled = false,
      type = "button",
      validity,
      size,
      ...props
    },
    ref,
  ) => {
    const context = useContext(FormContext);
    const actualValidity = validity || context?.validity;
    const [disabledButton, setDisabledButton] = useState(disabled);

    useEffect(() => {
      /**
       * Explicit disabled prop always wins.
       */
      if (disabled) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setDisabledButton(true);
        return;
      }

      /**
       * No form validity supplied.
       */
      if (!actualValidity) {
        setDisabledButton(false);
        return;
      }

      /**
       * Disable only when an actual field is invalid.
       */
      const hasInvalidField = Object.values(actualValidity).some(
        (value) => value === false,
      );

      setDisabledButton(hasInvalidField);
    }, [actualValidity, disabled]);

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabledButton}
        className={clsx(
          {
            "px-5": !className?.includes("px-"),
            "py-3": !className?.includes("py-"),
          },

          {
            "bg-primary text-white font-heebo font-medium":
              !className?.includes("bg-") && !disabledButton,
          },

          "shadow-sm hover:shadow rounded",

          `min-w-${block ? "full" : "max"}`,

          `min-h-${block ? "full" : "max"}`,

          {
            "bg-gray-100 text-gray-400 cursor-not-allowed": disabledButton,
          },

          !disabledButton &&
            variant &&
            `bg-${getTailwindColorClass(
              variant,
            )} text-white hover:bg-${getTailwindColorClass(variant)}-600`,

          "font-medium",

          className,
        )}
        {...props}
      >
        {children}
      </button>
    );
  },
);

Button.displayName = "Button";

export default Button;
